import { useMsal } from '@azure/msal-react';
import {
    Button,
    Checkbox,
    Input,
    makeStyles,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Textarea,
    tokens
} from '@fluentui/react-components';
import { Dismiss24Regular, Edit24Regular, Save24Regular } from '@fluentui/react-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useProject } from '../../context/ProjectContext';
import type { ProjektItem } from '../../models/ProjektItem';
import { DataService } from '../../services/dataService';
import useConfigStore from '../../store/config';

interface PermissionConfig {
    'Rolle Administratoren'?: 'r' | 'rw';
    'Rolle Hochschulleitung'?: 'r' | 'rw';
    'Rolle Controlling'?: 'r' | 'rw';
    'Rolle Accounting'?: 'r' | 'rw';
    'Rolle Vertragskoordination'?: 'r' | 'rw';
    'Rolle Forschungsservice'?: 'r' | 'rw';
    'Rolle Rektorat'?: 'r' | 'rw';
    isBenutzerProjektLeiter?: 'r' | 'rw';
    isBenutzerProjektleiterAssistent?: 'r' | 'rw';
    departmentHead?: 'r' | 'rw';
    isRegularUser?: 'r' | 'rw';
}

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalL,
    },
    headerTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorNeutralForeground1,
    },
    categorySection: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gap: tokens.spacingHorizontalL,
        marginBottom: tokens.spacingVerticalL,
        paddingBottom: tokens.spacingVerticalL,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    categoryHeader: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorBrandForeground1,
        paddingTop: '2px',
    },
    fieldsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
    },
    fieldBox: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gap: tokens.spacingHorizontalM,
        alignItems: 'start',
        paddingBottom: 0,
    },
    fieldLabel: {
        fontWeight: 600,
        fontSize: '11px',
        color: tokens.colorNeutralForeground2,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        paddingTop: '4px',
    },
    fieldValue: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
        wordWrap: 'break-word',
    },
    emptyValue: {
        color: tokens.colorNeutralForeground3,
        fontStyle: 'italic',
    },
    booleanValue: {
        padding: '2px 6px',
        borderRadius: '2px',
        fontSize: '11px',
        fontWeight: 600,
        display: 'inline-block',
        width: 'fit-content',
    },
    booleanTrue: {
        backgroundColor: tokens.colorStatusSuccessBackground2,
        color: tokens.colorStatusSuccessForeground1,
    },
    booleanFalse: {
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground3,
    },
    inputField: {
        width: '100%',
        fontSize: tokens.fontSizeBase300,
    },
    link: {
        color: tokens.colorBrandForeground1,
        textDecoration: 'none',
        cursor: 'pointer',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    footerBar: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginTop: tokens.spacingVerticalXXL,
        paddingTop: tokens.spacingVerticalL,
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
        gap: tokens.spacingHorizontalM,
    },
    placeholder: {
        padding: tokens.spacingVerticalXL,
        textAlign: 'center',
        color: tokens.colorNeutralForeground2,
    },
});

interface EckdatenWebsiteProps {
    project: ProjektItem | null;
}

interface EditedValues {
    [key: string]: string | boolean | undefined;
}

interface FieldConfig {
    key: string;
    label: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value?: any;
    isBoolean?: boolean;
    isTextarea?: boolean;
    isLargeTextarea?: boolean;
    isLink?: boolean;
    editable?: boolean;
}

// Utility functions for permission checking
const canEditField = (
    fieldPermissions: PermissionConfig | undefined,
    userRole: string | null,
    project: ProjektItem | null
): boolean => {
    if (!fieldPermissions || !project) return false;

    // Check if user is a project manager
    if (project.isBenutzerProjektLeiter && fieldPermissions.isBenutzerProjektLeiter === 'rw') {
        return true;
    }

    // Check if user is a project manager assistant
    if (project.isBenutzerProjektleiterAssistent && fieldPermissions.isBenutzerProjektleiterAssistent === 'rw') {
        return true;
    }

    // Check role-based permissions for write access
    if (userRole === 'Administratoren' && fieldPermissions['Rolle Administratoren'] === 'rw') {
        return true;
    }

    return false;
};

const canViewField = (
    fieldPermissions: PermissionConfig | undefined,
    userRole: string | null,
    project: ProjektItem | null
): boolean => {
    if (!fieldPermissions || !project) return false;

    // Check if user is a project manager
    if (project.isBenutzerProjektLeiter &&
        (fieldPermissions.isBenutzerProjektLeiter === 'rw' || fieldPermissions.isBenutzerProjektLeiter === 'r')) {
        return true;
    }

    // Check if user is a project manager assistant
    if (project.isBenutzerProjektleiterAssistent &&
        (fieldPermissions.isBenutzerProjektleiterAssistent === 'rw' || fieldPermissions.isBenutzerProjektleiterAssistent === 'r')) {
        return true;
    }

    // Check role-based permissions
    if (userRole === 'Administratoren' && (fieldPermissions['Rolle Administratoren'] === 'rw' || fieldPermissions['Rolle Administratoren'] === 'r')) {
        return true;
    }

    if (userRole === 'Hochschulleitung' && (fieldPermissions['Rolle Hochschulleitung'] === 'rw' || fieldPermissions['Rolle Hochschulleitung'] === 'r')) {
        return true;
    }

    if (userRole === 'Controlling' && (fieldPermissions['Rolle Controlling'] === 'rw' || fieldPermissions['Rolle Controlling'] === 'r')) {
        return true;
    }

    if (userRole === 'Accounting' && (fieldPermissions['Rolle Accounting'] === 'rw' || fieldPermissions['Rolle Accounting'] === 'r')) {
        return true;
    }

    if (userRole === 'Vertragskoordination' && (fieldPermissions['Rolle Vertragskoordination'] === 'rw' || fieldPermissions['Rolle Vertragskoordination'] === 'r')) {
        return true;
    }

    if (userRole === 'Forschungsservice' && (fieldPermissions['Rolle Forschungsservice'] === 'rw' || fieldPermissions['Rolle Forschungsservice'] === 'r')) {
        return true;
    }

    if (userRole === 'Rektorat' && (fieldPermissions['Rolle Rektorat'] === 'rw' || fieldPermissions['Rolle Rektorat'] === 'r')) {
        return true;
    }

    // Check if user is a regular user (fallback for users without special roles)
    if (fieldPermissions.isRegularUser === 'r' || fieldPermissions.isRegularUser === 'rw') {
        return true;
    }

    return false;
};

const initializeEditedValues = (project: ProjektItem | null): EditedValues => {
    if (!project) return {};
    return {
        veroeffentlichungWebsite: project.veroeffentlichungWebsite || false,
        websiteFreigabeForschung: project.websiteFreigabeForschung || false,
        websiteFreigabeMarketing: project.websiteFreigabeMarketing || false,
        websiteAblehnungForschungKommentar: project.websiteAblehnungForschungKommentar || '',
        websiteProjekttitelDeutsch: project.websiteProjekttitelDeutsch || '',
        websiteProjekttitelEnglisch: project.websiteProjekttitelEnglisch || '',
        websiteProjektbeschreibungDeutsch: project.websiteProjektbeschreibungDeutsch || '',
        websiteProjektbeschreibungEnglisch: project.websiteProjektbeschreibungEnglisch || '',
        websiteAuftraggeberURL: project.websiteAuftraggeberURL || '',
        websiteProjektURL: project.websiteProjektURL || '',
        websiteSchlagworte: project.websiteSchlagworte || '',
        websiteKeywords: project.websiteKeywordsAsText || '',
    };
};

const EckdatenWebsite: React.FC<EckdatenWebsiteProps> = ({ project }) => {
    const styles = useStyles();
    const { selectedRole } = useConfigStore((state) => state.roleConfig);
    const { accounts, instance } = useMsal();
    const { refreshProject } = useProject();
    const [editedValues, setEditedValues] = useState<EditedValues>(() => initializeEditedValues(project));
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // DataService instance
    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Centralized field permissions for all Website fields
    const allFieldPermissions = useMemo(() => {
        const permissions: Array<{ key: string; permissions: PermissionConfig }> = [
            { key: 'veroeffentlichungWebsite', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', isRegularUser: 'r' } },
            { key: 'websiteFreigabeForschung', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', isRegularUser: 'r' } },
            { key: 'websiteFreigabeMarketing', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', isRegularUser: 'r' } },
            { key: 'websiteAblehnungForschungKommentar', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', isRegularUser: 'r' } },
            { key: 'websiteProjekttitelDeutsch', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', isRegularUser: 'r' } },
            { key: 'websiteProjekttitelEnglisch', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', isRegularUser: 'r' } },
            { key: 'websiteProjektbeschreibungDeutsch', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', isRegularUser: 'r' } },
            { key: 'websiteProjektbeschreibungEnglisch', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', isRegularUser: 'r' } },
            { key: 'websiteAuftraggeberURL', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', isRegularUser: 'r' } },
            { key: 'websiteProjektURL', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', isRegularUser: 'r' } },
            { key: 'websiteSchlagworte', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', isRegularUser: 'r' } },
            { key: 'websiteKeywords', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', isRegularUser: 'r' } },
        ];
        return permissions;
    }, []);

    // Check if user can edit any field
    const canEditAny = useMemo(() => {
        if (!project) return false;
        return allFieldPermissions.some((field) => {
            return canEditField(field.permissions, selectedRole, project);
        });
    }, [project, selectedRole, allFieldPermissions]);

    useEffect(() => {
        setEditedValues(initializeEditedValues(project));
    }, [project]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleFieldChange = (key: string, value: any) => {
        setEditedValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSave = async () => {
        if (!project || !dataService) return;

        setIsSaving(true);
        try {
            const updatedProject: ProjektItem = {
                ...project,
                veroeffentlichungWebsite: editedValues.veroeffentlichungWebsite as boolean,
                websiteFreigabeForschung: editedValues.websiteFreigabeForschung as boolean,
                websiteFreigabeMarketing: editedValues.websiteFreigabeMarketing as boolean,
                websiteAblehnungForschungKommentar: editedValues.websiteAblehnungForschungKommentar as string,
                websiteProjekttitelDeutsch: editedValues.websiteProjekttitelDeutsch as string,
                websiteProjekttitelEnglisch: editedValues.websiteProjekttitelEnglisch as string,
                websiteProjektbeschreibungDeutsch: editedValues.websiteProjektbeschreibungDeutsch as string,
                websiteProjektbeschreibungEnglisch: editedValues.websiteProjektbeschreibungEnglisch as string,
                websiteAuftraggeberURL: editedValues.websiteAuftraggeberURL as string,
                websiteProjektURL: editedValues.websiteProjektURL as string,
                websiteSchlagworte: editedValues.websiteSchlagworte as string,
            };

            const result = await dataService.updateProjekt(updatedProject);

            if (result.success) {
                if (refreshProject) {
                    await refreshProject();
                }
                setShowSuccessMessage(true);
                setErrorMessage(null);
                setTimeout(() => setShowSuccessMessage(false), 3000);
                setIsEditing(false);
            } else {
                setShowSuccessMessage(false);
                setErrorMessage(result.message);
                setTimeout(() => setErrorMessage(null), 5000);
            }
        } catch (error) {
            console.error('Error saving changes:', error);
            setShowSuccessMessage(false);
            setErrorMessage('Error saving. Please try again.');
            setTimeout(() => setErrorMessage(null), 5000);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (isSaving) return;
        setEditedValues(initializeEditedValues(project));
        setIsEditing(false);
    };

    const renderDisplayField = (config: FieldConfig) => {
        const displayValue = config.value;
        let valueContent: React.ReactNode = null;

        if (displayValue === null || displayValue === undefined || displayValue === '') {
            valueContent = <div className={`${styles.fieldValue} ${styles.emptyValue}`}>-</div>;
        } else if (config.isBoolean) {
            const isTrue = displayValue === true || displayValue === 'Yes' || displayValue === 'true' || displayValue === 1;
            valueContent = (
                <div className={`${styles.booleanValue} ${isTrue ? styles.booleanTrue : styles.booleanFalse}`}>
                    {isTrue ? 'Yes' : 'No'}
                </div>
            );
        } else if (config.isLink) {
            valueContent = displayValue ? (
                <a href={displayValue} target="_blank" rel="noopener noreferrer" className={styles.link}>
                    {displayValue}
                </a>
            ) : (
                <div className={`${styles.fieldValue} ${styles.emptyValue}`}>-</div>
            );
        } else {
            valueContent = <div className={styles.fieldValue} style={{ whiteSpace: config.isTextarea ? 'pre-wrap' : 'normal' }}>{displayValue}</div>;
        }

        return (
            <div className={styles.fieldBox}>
                <div className={styles.fieldLabel}>{config.label}</div>
                {valueContent}
            </div>
        );
    };

    const renderEditField = (config: FieldConfig) => {
        const value = String(editedValues[config.key] || '');

        if (config.isBoolean) {
            const isTrue = value === 'Yes' || value === 'true' || value === '1' || editedValues[config.key] === true;
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{config.label}</div>
                    <div style={{ marginTop: '2px' }}>
                        <Checkbox
                            checked={isTrue}
                            onChange={(_ev, data) => {
                                handleFieldChange(config.key, data.checked);
                            }}
                        />
                    </div>
                </div>
            );
        }

        if (config.isLargeTextarea) {
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{config.label}</div>
                    <Textarea
                        value={value}
                        onChange={(_ev, data) => {
                            handleFieldChange(config.key, data.value);
                        }}
                        className={styles.inputField}
                        resize="vertical"
                        textarea={{
                            style: {
                                minHeight: '120px',
                                maxHeight: 'none',
                            },
                        }}
                    />
                </div>
            );
        }

        if (config.isTextarea) {
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{config.label}</div>
                    <Textarea
                        value={value}
                        onChange={(_ev, data) => {
                            handleFieldChange(config.key, data.value);
                        }}
                        className={styles.inputField}
                    />
                </div>
            );
        }

        return (
            <div className={styles.fieldBox}>
                <div className={styles.fieldLabel}>{config.label}</div>
                <Input
                    value={value}
                    onChange={(_ev, data) => {
                        handleFieldChange(config.key, data.value);
                    }}
                    className={styles.inputField}
                />
            </div>
        );
    };

    const renderField = (config: FieldConfig) => {
        // Find field permissions
        const fieldPermConfig = allFieldPermissions.find((f) => f.key === config.key);
        const canEdit = fieldPermConfig ? canEditField(fieldPermConfig.permissions, selectedRole, project) : false;
        const canView = fieldPermConfig ? canViewField(fieldPermConfig.permissions, selectedRole, project) : false;

        // If user cannot view this field, show nothing
        if (!canView) {
            return null;
        }

        // If editing and can edit, show edit field
        if (isEditing && canEdit && config.editable !== false) {
            return renderEditField(config);
        }
        return renderDisplayField(config);
    };

    if (!project) {
        return (
            <div className={styles.container}>
                <div className={styles.placeholder}>
                    <p>No project data available</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {isSaving && (
                <MessageBar>
                    <MessageBarBody>
                        <MessageBarTitle>Saving...</MessageBarTitle>
                        Please wait while your changes are being saved.
                    </MessageBarBody>
                </MessageBar>
            )}
            {showSuccessMessage && (
                <MessageBar intent="success">
                    <MessageBarBody>
                        <MessageBarTitle>Success</MessageBarTitle>
                        Website information saved successfully!
                    </MessageBarBody>
                </MessageBar>
            )}
            {errorMessage && (
                <MessageBar intent="error">
                    <MessageBarBody>
                        <MessageBarTitle>Error</MessageBarTitle>
                        {errorMessage}
                    </MessageBarBody>
                </MessageBar>
            )}
            {/* Header with Edit Button */}
            {canEditAny && (
                <div className={styles.header}>
                    <div className={styles.headerTitle}>Website Information</div>
                    <div>
                        {isEditing ? (
                            <div style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
                                <Button
                                    appearance="primary"
                                    icon={<Save24Regular />}
                                    onClick={handleSave}
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                    appearance="secondary"
                                    icon={<Dismiss24Regular />}
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                            </div>
                        ) : (
                            <Button
                                appearance="outline"
                                icon={<Edit24Regular />}
                                onClick={() => setIsEditing(true)}
                            >
                                Edit
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Publication Data Section */}
            <div className={styles.categorySection}>
                <div className={styles.categoryHeader}>Publication Data</div>
                <div className={styles.fieldsContainer}>
                    {renderField({ key: 'veroeffentlichungWebsite', label: 'Publication on FH Website', value: project.veroeffentlichungWebsite, isBoolean: true, editable: true })}
                    {renderField({ key: 'websiteFreigabeForschung', label: 'Release by Research Service', value: project.websiteFreigabeForschung, isBoolean: true, editable: true })}
                    {renderField({ key: 'websiteFreigabeMarketing', label: 'Release by Marketing', value: project.websiteFreigabeMarketing, isBoolean: true, editable: true })}
                    {renderField({ key: 'websiteAblehnungForschungKommentar', label: 'Comment', value: editedValues.websiteAblehnungForschungKommentar, isLargeTextarea: true, editable: true })}
                </div>
            </div>

            {/* Advertisement Data Section */}
            <div className={styles.categorySection}>
                <div className={styles.categoryHeader}>Advertisement Data</div>
                <div className={styles.fieldsContainer}>
                    {renderField({ key: 'websiteProjekttitelDeutsch', label: 'Project Title (German)', value: editedValues.websiteProjekttitelDeutsch, editable: true })}
                    {renderField({ key: 'websiteProjekttitelEnglisch', label: 'Project Title (English)', value: editedValues.websiteProjekttitelEnglisch, editable: true })}
                    {renderField({ key: 'websiteProjektbeschreibungDeutsch', label: 'Project Description (German)', value: editedValues.websiteProjektbeschreibungDeutsch, isLargeTextarea: true, editable: true })}
                    {renderField({ key: 'websiteProjektbeschreibungEnglisch', label: 'Project Description (English)', value: editedValues.websiteProjektbeschreibungEnglisch, isLargeTextarea: true, editable: true })}
                    {renderField({ key: 'websiteAuftraggeberURL', label: 'Funder/Client Website', value: editedValues.websiteAuftraggeberURL, isLink: !isEditing, editable: true })}
                    {renderField({ key: 'websiteProjektURL', label: 'Project Website', value: editedValues.websiteProjektURL, isLink: !isEditing, editable: true })}
                </div>
            </div>

            {/* Keywords Section */}
            <div className={styles.categorySection}>
                <div className={styles.categoryHeader}>Keywords</div>
                <div className={styles.fieldsContainer}>
                    {renderField({ key: 'websiteSchlagworte', label: 'Keywords (German)', value: editedValues.websiteSchlagworte, isLargeTextarea: true, editable: true })}
                    {renderField({ key: 'websiteKeywords', label: 'Keywords (English)', value: editedValues.websiteKeywords, isLargeTextarea: true, editable: true })}
                </div>
            </div>

            {/* Footer with Save Button */}
            {canEditAny && isEditing && (
                <div className={styles.footerBar}>
                    <Button
                        appearance="primary"
                        icon={<Save24Regular />}
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        Save
                    </Button>
                    <Button
                        appearance="secondary"
                        icon={<Dismiss24Regular />}
                        onClick={handleCancel}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                </div>
            )}
        </div>
    );
};

export default EckdatenWebsite;
